/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/*
 * Developer note! When updating this file, make sure to also update the
 * _app-config template files in pwa-kit-create-app.
 *
 * In the pwa-kit-create-app, the templates are found under:
 * - assets/bootstrap/js/overrides/app/components/_app-config
 * - assets/templates/@salesforce/retail-react-app/app/components/_app-config
 */
import React from 'react'
import PropTypes from 'prop-types'
import {ChakraProvider} from '@salesforce/retail-react-app/app/components/shared/ui'
import Cookies from 'js-cookie'

// Removes focus for non-keyboard interactions for the whole application
import 'focus-visible/dist/focus-visible'

import theme from '../../theme'
import {MultiSiteProvider} from '../../contexts'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {resolveSiteFromUrl, resolveLocaleFromUrl, getLocaleByReference} from '@app/utils/site-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import createLogger from '@salesforce/pwa-kit-runtime/utils/logger-factory'

import {CommerceApiProvider} from '@salesforce/commerce-sdk-react'
import {withReactQuery} from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/with-react-query'
import {useCorrelationId} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {ReactQueryDevtools} from '@tanstack/react-query-devtools'
import {COOKIE, DEFAULT_DNT_STATE} from '../../constants'

import {LocalFonts} from '../_app/partials/fonts'
import {HelperClasses} from '../_app/partials/helper-classes'
import {
    DeliveryCountryDecisionProvider,
    HeaderBannerStateProvider,
    SearchRedirectsProvider,
    ShipToCountryProvider
} from '../../custom-contexts'
import {getShipToCountry, resolveCountryFromUrl} from '../../utils/ship-to-country-utils'
import {normalizeCountryCode} from '@app/utils/delivery-country-decision'
import {getClosestLocale, isServer} from '@app/utils/utils'
import {createUrlTemplate} from '@app/utils/url'
import {getSiteSearchRedirects} from '@app/utils/search-redirects'

/**
 * Use the AppConfig component to inject extra arguments into the getProps
 * methods for all Route Components in the app – typically you'd want to do this
 * to inject a connector instance that can be used in all Pages.
 *
 * You can also use the AppConfig to configure a state-management library such
 * as Redux, or Mobx, if you like.
 */
const AppConfig = ({children, locals = {}}) => {
    const {correlationId} = useCorrelationId()
    const headers = {
        'correlation-id': correlationId,
        c_locale: locals.locale?.id
    }

    const commerceApiConfig = locals.appConfig.commerceAPI

    const appOrigin = useAppOrigin()

    const passwordlessCallback = locals.appConfig.login?.passwordless?.callbackURI

    return (
        <CommerceApiProvider
            shortCode={commerceApiConfig.parameters.shortCode}
            clientId={commerceApiConfig.parameters.clientId}
            organizationId={commerceApiConfig.parameters.organizationId}
            siteId={locals.site?.id}
            locale={locals.locale?.id}
            currency={locals.shipToCountry?.currency}
            redirectURI={`${appOrigin}/callback`}
            passwordlessLoginCallbackURI={passwordlessCallback}
            proxy={`${appOrigin}${commerceApiConfig.proxyPath}`}
            headers={headers}
            defaultDnt={DEFAULT_DNT_STATE}
            // Uncomment 'enablePWAKitPrivateClient' to use SLAS private client login flows.
            // Make sure to also enable useSLASPrivateClient in ssr.js when enabling this setting.
            enablePWAKitPrivateClient={true}
            privateClientProxyEndpoint={`${appOrigin}/mobify/slas/private`}
            logger={createLogger({packageName: 'commerce-sdk-react'})}
        >
            <DeliveryCountryDecisionProvider
                deliveryCountryDecision={locals.deliveryCountryDecision}
            >
                <MultiSiteProvider
                    site={locals.site}
                    locale={locals.locale}
                    buildUrl={locals.buildUrl}
                >
                    <SearchRedirectsProvider searchRedirects={locals.searchRedirects}>
                        <ShipToCountryProvider shipToCountry={locals.shipToCountry}>
                            <HeaderBannerStateProvider
                                headerBannerState={locals.headerBannerState}
                            >
                                <ChakraProvider theme={theme}>
                                    <HelperClasses />
                                    <LocalFonts />
                                    {children}
                                </ChakraProvider>
                            </HeaderBannerStateProvider>
                        </ShipToCountryProvider>
                    </SearchRedirectsProvider>
                </MultiSiteProvider>
            </DeliveryCountryDecisionProvider>
            <ReactQueryDevtools />
        </CommerceApiProvider>
    )
}

AppConfig.restore = (locals = {}, frozen) => {
    const path =
        typeof window === 'undefined'
            ? locals.originalUrl
            : `${window.location.pathname}${window.location.search}`
    const {app: appConfig} = getConfig()

    const site = resolveSiteFromUrl(path)

    // Gets locale from URL, ex: `/eu/fr/`
    const localeFromPath = resolveLocaleFromUrl(path)
    // Gets locale from the `userSetLocale` cookie
    const userSetLocale = getLocaleByReference(
        site,
        isServer ? locals.localization.userSetLocale : Cookies.get(COOKIE.USER_SET_LOCALE)
    )
    // Searches for a locale close to the user's preferred locales
    const clientPreferredLocale = getClosestLocale(
        site,
        isServer ? locals.localization.clientPreferredLocales : navigator.languages
    )
    // Default site locale
    const defaultSiteLocale = getLocaleByReference(site, site.l10n.defaultLocale)

    const deliveryCountryDecision = isServer
        ? locals.deliveryCountryDecision
        : frozen?.deliveryCountryDecision
    const localeFromDecision = getLocaleByReference(site, deliveryCountryDecision?.suggestedLocale)

    const locale =
        localeFromPath ??
        userSetLocale ??
        clientPreferredLocale ??
        localeFromDecision ??
        defaultSiteLocale

    // get country from url
    const country = normalizeCountryCode(resolveCountryFromUrl(path))
    const countryFromDecision = normalizeCountryCode(deliveryCountryDecision?.shipToCountry?.id)
    const acceptedCountryFromCookie = normalizeCountryCode(
        !isServer ? Cookies.get(COOKIE.SHIP_TO_COUNTRY_ACCEPTED) : null
    )
    const isUSSite = site?.id.includes('US')
    const acceptedCountryMatchesSite =
        acceptedCountryFromCookie &&
        getShipToCountry(acceptedCountryFromCookie, isUSSite) &&
        (acceptedCountryFromCookie === 'US') === isUSSite
    const siteDefaultCountry = site?.id.includes('US') ? 'US' : 'FR'
    const resolvedCountry =
        country ||
        (acceptedCountryMatchesSite ? acceptedCountryFromCookie : null) ||
        countryFromDecision ||
        siteDefaultCountry

    const headerBannerState = isServer
        ? locals.headerBannerState
        : Cookies.get(COOKIE.HEADER_BANNER_STATE)

    const apiConfig = {
        ...appConfig.commerceAPI,
        einsteinConfig: appConfig.einsteinAPI
    }

    apiConfig.parameters.siteId = site.id

    locals.buildUrl = createUrlTemplate(appConfig, site.alias || site.id, locale.id)
    locals.site = site
    locals.locale = locale
    locals.appConfig = appConfig
    locals.deliveryCountryDecision = deliveryCountryDecision
    locals.searchRedirects = isServer
        ? getSiteSearchRedirects(process.env.SEARCH_REDIRECTS, site.id)
        : frozen?.searchRedirects ?? []

    // Resolve from explicit URL country first, then the SSR delivery decision, then the site default.
    locals.shipToCountry =
        getShipToCountry(resolvedCountry, isUSSite) || getShipToCountry(null, isUSSite)
    locals.headerBannerState = headerBannerState
    locals.cspNonce = locals?.cspNonce ?? frozen?.cspNonce

    apiConfig.parameters.cspNonce = locals.cspNonce
}

AppConfig.freeze = (locals = {}) => {
    return {
        cspNonce: locals.cspNonce,
        deliveryCountryDecision: locals.deliveryCountryDecision,
        searchRedirects: locals.searchRedirects
    }
}

AppConfig.extraGetPropsArgs = (locals = {}) => {
    return {
        buildUrl: locals.buildUrl,
        site: locals.site,
        locale: locals.locale,
        shipToCountry: locals.shipToCountry,
        deliveryCountryDecision: locals.deliveryCountryDecision,
        searchRedirects: locals.searchRedirects
    }
}

AppConfig.propTypes = {
    children: PropTypes.node,
    locals: PropTypes.object
}

// Recommended settings for PWA-Kit usages.
// NOTE: they will be applied on both server and client side.
// retry is always disabled on server side regardless of the value from the options
const options = {
    queryClientConfig: {
        defaultOptions: {
            queries: {
                retry: false,
                refetchOnWindowFocus: false,
                // staleTime is the amount of time before a cached query is considered
                // In SIG or Staging we want this value to be 0
                // Development and Production to have the default PWA Kit value
                staleTime: 10 * 1000,
                ...(isServer ? {retryOnMount: false} : {})
            },
            mutations: {
                retry: false
            }
        }
    },
    beforeHydrate: (data) => {
        const now = Date.now()

        // Helper to reset the data timestamp to time of app load.
        const updateQueryTimeStamp = ({state}) => {
            state.dataUpdatedAt = now
        }

        // Update serialized mutations and queries to ensure that the cached data is
        // considered fresh on first load.
        data?.mutations?.forEach(updateQueryTimeStamp)
        data?.queries?.forEach(updateQueryTimeStamp)

        return data
    }
}

export default withReactQuery(AppConfig, options)
